import { z } from "zod";
import type { HexColor } from "./hex-color/hex-color";
import { type HSLA, partialHslaZodSchema } from "./hsla-color/hsla-color";

type ColorValue = {
    readonly _type: "COLOR_VALUE";
    readonly isLight: boolean;
    readonly isDark: boolean;
    readonly asCssHsla: string;
    readonly asRgba: string;
    readonly rgba: [red: number, green: number, blue: number, alpha: number];
    readonly asHex: HexColor;
    readonly asString: string;
    readonly hue: number;
    readonly saturation: number;
    readonly light: number;
    readonly alpha: number;
    /**
     * `alpha` value between 0 and 1
     */
    withAlpha: (alpha: number) => ColorValue;
    /**
     * `light` value between 0 and 1
     */
    withLight: (light: number) => ColorValue;
    /**
     * `hue` value between 0 and 360
     */
    withHue: (hue: number) => ColorValue;
    /**
     * `saturation` value between 0 and 1
     */
    withSaturation: (saturation: number) => ColorValue;
    /**
     * `hue` value between 0 and 360
     * `saturation` value between 0 and 1
     * `light` value between 0 and 1
     * `alpha` value between 0 and 1
     */
    withModifications: (options: Partial<HSLA>) => ColorValue;
    withRelativeModifications: (options: Partial<HSLA>) => ColorValue;
};

const colorValueZodSchema: z.ZodSchema<ColorValue> = z.object({
    _type: z.literal("COLOR_VALUE"),
    alpha: z.number().min(0).max(1),
    asCssHsla: z.string(),
    asRgba: z.string(),
    rgba: z.tuple([
        z.number().min(0).max(255),
        z.number().min(0).max(255),
        z.number().min(0).max(255),
        z.number().min(0).max(1),
    ]),
    asString: z.string(),
    asHex: z.string() as unknown as z.ZodSchema<HexColor>,
    hue: z
        .number()
        .min(0)
        .max(360)
        .transform(value => Math.round(value)),
    isDark: z.boolean(),
    isLight: z.boolean(),
    light: z.number().min(0).max(1),
    saturation: z.number().min(0).max(1),
    withAlpha: z.lazy(() =>
        z
            .function()
            .args(z.number().min(0).max(1))
            .returns(colorValueZodSchema),
    ),
    withLight: z.lazy(() =>
        z
            .function()
            .args(z.number().min(0).max(1))
            .returns(colorValueZodSchema),
    ),
    withHue: z.lazy(() =>
        z
            .function()
            .args(z.number().min(0).max(360))
            .returns(colorValueZodSchema),
    ),
    withSaturation: z.lazy(() =>
        z
            .function()
            .args(z.number().min(0).max(1))
            .returns(colorValueZodSchema),
    ),
    withModifications: z.lazy(() =>
        z.function().args(partialHslaZodSchema).returns(colorValueZodSchema),
    ),
    withRelativeModifications: z.lazy(() =>
        z.function().args(partialHslaZodSchema).returns(colorValueZodSchema),
    ),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isColorValue = (value: any): value is ColorValue => {
    return value?._type === "COLOR_VALUE";
};

export { colorValueZodSchema, isColorValue };
export type { ColorValue };
